import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { AuditTrail, type AuditEntryView } from "@/components/admin/audit-trail";
import { DeliveryForm, type DeliveryInitial } from "@/components/admin/delivery-form";
import { OrderHeader, type OrderDetail } from "@/components/admin/order-header";
import { OrderSidebar, type OrderFeedback } from "@/components/admin/order-sidebar";
import { RevisionRequests, type RevisionRequestRow } from "@/components/admin/revision-requests";
import { UpdatesThread, type UpdateView } from "@/components/admin/updates-thread";
import { AdminShell } from "@/components/admin/admin-shell";
import { BriefDisplay } from "@/components/brief/BriefDisplay";
import { Card } from "@/components/ui/card";
import { LocalDateTime } from "@/components/ui/local-datetime";
import type { BriefSummary } from "@/lib/brief";
import type { DeliveryAssetType } from "@/components/admin/asset-editor";

type OrderDetailResponse = {
  order: OrderDetail;
  delivery: {
    livePreviewUrl: string;
    instructions: string;
    loomUrl: string;
    deliveredAt: string;
    assets: {
      id: string;
      type: DeliveryAssetType;
      label: string;
      value: string;
      sensitive: boolean;
    }[];
  } | null;
  updates: UpdateView[];
  audit: AuditEntryView[];
  revisionRequests: RevisionRequestRow[];
  repliesByRequestId: Record<string, { message: string; createdAt: string }>;
  etaByRequestId: Record<string, { hours: number; startedAt: string; targetAt: string }>;
  feedback: OrderFeedback | null;
  briefSummary: BriefSummary;
};

export function AdminOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [data, setData] = useState<OrderDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`/api/admin/orders/${orderId}/detail`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 404) throw new Error("Order not found");
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<OrderDetailResponse>;
      })
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });

    return () => controller.abort();
  }, [orderId]);

  if (loading) {
    return (
      <AdminShell>
        <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
            <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading order details…
            </div>

            <div className="space-y-4">
              <div className="brand-skeleton h-4 w-32 rounded-md" />
              <div className="brand-skeleton h-8 w-72 rounded-lg" />
              <div className="brand-skeleton h-4 w-56 rounded-md" />
            </div>

            <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
              <div className="space-y-6">
                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="space-y-3">
                    <div className="brand-skeleton h-4 w-40 rounded-md" />
                    <div className="brand-skeleton h-24 w-full rounded-xl" />
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="space-y-3">
                    <div className="brand-skeleton h-4 w-36 rounded-md" />
                    <div className="brand-skeleton h-28 w-full rounded-xl" />
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="space-y-3">
                    <div className="brand-skeleton h-4 w-32 rounded-md" />
                    <div className="brand-skeleton h-20 w-full rounded-xl" />
                  </div>
                </div>
              </div>

              <aside className="space-y-4">
                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="space-y-3">
                    <div className="brand-skeleton h-4 w-24 rounded-md" />
                    <div className="brand-skeleton h-3 w-full rounded-md" />
                    <div className="brand-skeleton h-3 w-3/4 rounded-md" />
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="space-y-3">
                    <div className="brand-skeleton h-4 w-20 rounded-md" />
                    <div className="brand-skeleton h-3 w-full rounded-md" />
                    <div className="brand-skeleton h-3 w-5/6 rounded-md" />
                    <div className="brand-skeleton h-3 w-2/3 rounded-md" />
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>
      </AdminShell>
    );
  }

  if (error || !data) {
    return (
      <AdminShell>
        <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
              {error ?? "Order not found"}
            </div>
          </div>
        </main>
      </AdminShell>
    );
  }

  const {
    order,
    delivery,
    updates,
    audit,
    feedback,
    briefSummary,
  } = data;
  const revisionRequests = Array.isArray(data.revisionRequests)
    ? data.revisionRequests
    : [];
  const repliesByRequestId = data.repliesByRequestId ?? {};
  const etaByRequestId = data.etaByRequestId ?? {};

  const deliveryInitial: DeliveryInitial = {
    livePreviewUrl: delivery?.livePreviewUrl ?? "",
    instructions: delivery?.instructions ?? "",
    loomUrl: delivery?.loomUrl ?? "",
    assets: delivery?.assets ?? [],
  };

  return (
    <AdminShell>
      <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
          <OrderHeader order={order} hasDelivery={delivery !== null} />

          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="space-y-6">
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-muted-foreground">
                  Customer brief
                </h2>
                <BriefDisplay
                  summary={briefSummary}
                  variant="detail"
                  renderTarget="admin"
                />
              </section>

              <RevisionRequests
                orderId={order.id}
                requests={revisionRequests}
                repliesByRequestId={repliesByRequestId}
                etaByRequestId={etaByRequestId}
              />

              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-muted-foreground">
                  Updates
                </h2>
                <UpdatesThread
                  orderId={order.id}
                  initialUpdates={updates}
                />
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-muted-foreground">
                  Delivery
                </h2>
                <DeliveryForm
                  orderId={order.id}
                  initial={deliveryInitial}
                />
                {delivery ? (
                  <Card className="px-4 py-2 text-xs text-muted-foreground">
                    Delivery first saved{" "}
                    <LocalDateTime iso={delivery.deliveredAt} preset="medium" />.
                  </Card>
                ) : null}
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-muted-foreground">
                  History
                </h2>
                <AuditTrail entries={audit} />
              </section>
            </div>

            <aside className="lg:sticky lg:top-6 lg:self-start">
              <OrderSidebar order={order} feedback={feedback} />
            </aside>
          </div>
        </div>
      </main>
    </AdminShell>
  );
}
